import { Progress } from "../progress"
import React, { useEffect, useMemo, useState } from "react"
import { XIcon } from "lucide-react"
import { ProgressBar } from "../../../components/ui-primitives/progress-bar"
import { mergeClasses } from "../../../helpers/styling"
import { mapRecordToList } from "../../../utils/typescript"
import { useFadePresence } from "../../../hooks/use-fade-presence"
import { useLatest } from "../../../hooks/use-latest"
import { summarizeProgress } from "./summarize-progress"

export function ProgressDisplay<S extends string>(props: {
  theme?: "light" | "dark"
  progress: Progress<S>
  stageLabels: Record<S, string>
  stageDurationBias?: Partial<Record<S, number>>
  onHidden?: () => void
} & React.HTMLAttributes<HTMLDivElement>) {
  const latestOnHidden = useLatest(props.onHidden)
  const { progress, stageLabels, stageDurationBias, onHidden, ...otherProps } = props
  const summary = useMemo(() => {
    return summarizeProgress(props.progress, props.stageDurationBias)
  }, [
    props.progress,
    props.stageDurationBias
  ])

  const { visible, opaque } = useFadePresence(
    summary.status !== "disabled" && summary.status !== "done", {
      duration: 300,
      delay: summary.status === "done" ? 500 : 0,
    }
  )

  const [ dismissed, setDismissed ] = useState(false)
  const shouldRender = visible && !dismissed

  useEffect(() => setDismissed(false), [ props.progress.id ])
  useEffect(() => {
    if (shouldRender) return
    latestOnHidden.current?.()
  }, [ latestOnHidden, shouldRender ])

  if (!shouldRender) return null

  return (
    <div {...otherProps}
         className={mergeClasses(
           `flex flex-col relative gap-1.5
           p-2.5 rounded-lg bg-gray-200
           transition-opacity duration-300`, {
             "opacity-100": opaque,
             "opacity-0 pointer-events-none": !opaque,
             "bg-gray-800 border !border-gray-600 text-white": props.theme === "dark",
             "bg-neutral-200 text-black": props.theme === "light"
           },
           props.className)}>
      {summary.status === "error" && (
        <button title="Dismiss"
                onClick={() => setDismissed(true)}
                className="
                  absolute p-1 top-1 right-1
                  rounded-full opacity-50 cursor-pointer
                hover:bg-white hover:bg-opacity-10 hover:opacity-100">
          <XIcon size={12} />
        </button>
      )}

      <div className="flex flex-col gap-0.5 mb-1 items-center text-xs">
        {mapRecordToList(summary.activeItemsPerStage, stage => (
          <div key={stage}>
            {props.stageLabels[stage]}…
          </div>
        ))}
        {summary.status === "done" && (
          <div>Done!</div>
        )}
      </div>

      <ProgressBar
        value={summary.progress * 100}
        indicatorClassName={mergeClasses({
          "bg-red-500": summary.status === "error"
        })}
       />
      {summary.errors.length > 0 && (
        <div className="flex flex-col gap-1 pt-1">
          {summary.errors.map((progressError, idx) => (
            <div key={idx} className="text-xs text-red-500">
              <div>Error with {props.stageLabels[progressError.stage]} <FileLink url={progressError.item} /></div>
              <div>{progressError.error.message}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function FileLink(props: { url: string }) {
  const filename = props.url.split("/").pop() || props.url
  return (
    <a href={props.url} target="_blank" rel="noopener noreferrer" className="underline">
      {filename}
    </a>
  )
}
